/**
 * <s-faq-index> - Topic pills and search for the FAQ page.
 *
 * Without JavaScript the topic links are in-page anchors and every question
 * stays visible. With it:
 *   [data-faq-filter]  - Topic links; "all" shows every group.
 *   [data-faq-search]  - Filters questions by question and answer text.
 *   [data-faq-group]   - One section per topic.
 *   [data-faq-item]    - One <details> per question.
 *   [data-faq-empty]   - Shown when nothing matches.
 *   [data-faq-status]  - Polite live region with the visible question count.
 */
class SFaqIndex extends HTMLElement {
  #ac;
  #topic = 'all';
  #query = '';
  #wasSearching = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    for (const link of this.querySelectorAll('[data-faq-filter]')) {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.#topic = link.dataset.faqFilter || 'all';
        this.#render();
      }, opts);
    }

    const search = this.querySelector('[data-faq-search]');
    if (search) {
      const onSearch = () => {
        this.#query = search.value.trim().toLowerCase();
        if (this.#query) this.#topic = 'all';
        this.#render();
      };
      search.addEventListener('input', onSearch, opts);
      search.addEventListener('search', onSearch, opts);
      search.form?.addEventListener('submit', (event) => event.preventDefault(), opts);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  // Inline display wins over layout utilities (grid, flex) that would otherwise override [hidden].
  #toggle(element, show) {
    element.hidden = !show;
    element.style.display = show ? '' : 'none';
  }

  #render() {
    for (const link of this.querySelectorAll('[data-faq-filter]')) {
      if (link.dataset.faqFilter === this.#topic) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }

    let visible = 0;
    for (const group of this.querySelectorAll('[data-faq-group]')) {
      const topicMatch = this.#topic === 'all' || group.dataset.faqGroup === this.#topic;
      let groupVisible = 0;
      for (const item of group.querySelectorAll('[data-faq-item]')) {
        const text = item.textContent.toLowerCase();
        const show = topicMatch && (!this.#query || text.includes(this.#query));
        this.#toggle(item, show);
        if (show) groupVisible += 1;
        if (this.#query) item.open = show;
        else if (this.#wasSearching) item.open = false;
      }
      this.#toggle(group, groupVisible > 0);
      visible += groupVisible;
    }

    this.#wasSearching = Boolean(this.#query);

    const empty = this.querySelector('[data-faq-empty]');
    if (empty) this.#toggle(empty, visible === 0);
    const status = this.querySelector('[data-faq-status]');
    if (status) status.textContent = String(visible);
  }
}

customElements.define('s-faq-index', SFaqIndex);
